import React, { useState, useEffect, useCallback } from 'react';
import '../App.css';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { Select, Button, Modal, Typography, Row, Col } from 'antd';
import { FilterOutlined, ReloadOutlined } from '@ant-design/icons';

const { Title } = Typography;

const JobFilters = ({ apiUrl, onFilterApplied }) => {
  const [filters, setFilters] = useState({
    title: '',
    company: '',
    location: '',
  });
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const getJobBoardsForCountry = (countryName) => {
    let jobBoards = [];
    const country = countryName.toLowerCase();

    jobBoards.push("Indeed");

    const glassdoorCountries = [
      "australia", "austria", "belgium", "brazil", "canada", "france",
      "germany", "hong kong", "india", "ireland", "italy", "mexico",
      "netherlands", "new zealand", "singapore", "spain", "switzerland",
      "uk", "united kingdom", "usa", "united states", "us", "vietnam"
    ];
    if (glassdoorCountries.includes(country)) {
      jobBoards.push("Glassdoor");
    }

    jobBoards.push("LinkedIn");

    const baytCountries = [
      "bahrain", "kuwait", "oman", "qatar", "saudi arabia",
      "united arab emirates", "uae", "egypt", "morocco"
    ];
    if (baytCountries.includes(country)) {
      jobBoards.push("Bayt");
    }

    jobBoards.push("Google");

    return jobBoards.join(" → ");
  };

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setLoading(true);
        const companiesResponse = await axios.get(`${apiUrl}/companies`);
        if (companiesResponse.data?.companies) {
          setCompanies(companiesResponse.data.companies);
        }

        const locationsResponse = await axios.get(`${apiUrl}/locations`);
        if (locationsResponse.data?.locations) {
          setLocations(locationsResponse.data.locations);
        }

        const titlesResponse = await axios.get(`${apiUrl}/job-titles`);
        if (titlesResponse.data?.titles) {
          setJobTitles(titlesResponse.data.titles);
        }
      } catch (error) {
        console.error('Error fetching filter options:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFilterOptions();
  }, [apiUrl]);

  const debouncedApplyFilters = useCallback(
    debounce(async (filters) => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/jobs`, { params: filters });
        if (response.data?.jobs) {
          onFilterApplied(response.data.jobs);
        }
      } catch (error) {
        console.error('Error applying filters:', error);
      } finally {
        setLoading(false);
      }
    }, 300),
    [apiUrl, onFilterApplied]
  );

  const handleInputChange = (value, name) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    debouncedApplyFilters(newFilters);
  };

  const handleReset = () => {
    const resetFilters = { title: '', company: '', location: '' };
    setFilters(resetFilters);
    onFilterApplied(null);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <div className="job-filters-container">
      <Button
        type="primary"
        icon={<FilterOutlined />}
        onClick={showModal}
        className="filter-button"
      >
        Filters
      </Button>

      <Modal
        title="Job Filters"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="reset" onClick={handleReset} icon={<ReloadOutlined />}>
            Reset
          </Button>,
          <Button key="close" type="primary" onClick={handleCancel}>
            Close
          </Button>
        ]}
        className="filters-modal"
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <label className="filter-label">Job Title</label>
            <Select
              className="filter-select"
              placeholder="Select Job Title"
              value={filters.title || undefined}
              onChange={(value) => handleInputChange(value, 'title')}
              showSearch
              optionFilterProp="children"
              loading={loading}
            >
              <Select.Option value="">All Titles</Select.Option>
              {jobTitles.map((item, index) => (
                <Select.Option key={index} value={item.title}>
                  {item.title} ({item.count})
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col span={24}>
            <label className="filter-label">Company</label>
            <Select
              className="filter-select"
              placeholder="Select Company"
              value={filters.company || undefined}
              onChange={(value) => handleInputChange(value, 'company')}
              showSearch
              optionFilterProp="children"
              loading={loading}
            >
              <Select.Option value="">All Companies</Select.Option>
              {companies.map((company, index) => (
                <Select.Option key={index} value={company.name}>
                  {company.name} ({company.jobCount})
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col span={24}>
            <label className="filter-label">Location</label>
            <Select
              className="filter-select"
              placeholder="Select Location"
              value={filters.location || undefined}
              onChange={(value) => handleInputChange(value, 'location')}
              showSearch
              optionFilterProp="children"
              loading={loading}
            >
              <Select.Option value="">All Locations</Select.Option>
              {locations.map((location, index) => (
                <Select.Option key={index} value={location.name}>
                  {location.name} ({location.jobCount})
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>

        {filters.location && (
          <div className="job-boards-info">
            <strong>Job Boards:</strong> {getJobBoardsForCountry(filters.location)}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default JobFilters;
